import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { createSession, saveCompletedSession } from '../lib/firestore';
import { BALL_POINTS, BALL_HEX, computeSessionStats, generateInsights } from '../shared';
import type { Shot, Frame, BreakRecord, BallColor, SafetyOutcome, EscapeOutcome } from '../shared';
import { Button } from '../components/ui/Button';
import { InsightBox } from '../components/ui/Cards';
import {
  Crosshair, Target, Trophy, Circle, X, Shield, AlertTriangle,
  ArrowLeftRight, Lock, Shuffle, CheckCircle, XCircle,
  RotateCcw, ChevronRight,
} from 'lucide-react';

// null        = main shot grid
// 'ball'      = ball picker for regular pot
// 'longBall'  = ball picker for long pot (after confirming it went in)
// 'restBall'  = ball picker for rest pot (after confirming it went in)
// 'long'      = long pot outcome: pot or miss?
// 'rest'      = rest shot outcome: pot or miss?
// 'safety'    = safety outcome: good or poor?
// 'snooker'   = snooker outcome: escaped or fouled?
type Picker = 'ball' | 'longBall' | 'restBall' | 'long' | 'rest' | 'safety' | 'snooker' | null;

interface LoggerState {
  sessionId: string | null;
  started: boolean;
  shots: Shot[];
  frames: Frame[];
  currentBreak: number;
  currentBreakSeq: BallColor[];
  currentBreakNum: number;
  frameNum: number;
  pots: number;
  misses: number;
  safeties: number;
  fouls: number;
  startTime: number;
}

const emptyState = (): LoggerState => ({
  sessionId: null, started: false, shots: [], frames: [],
  currentBreak: 0, currentBreakSeq: [], currentBreakNum: 0,
  frameNum: 1, pots: 0, misses: 0, safeties: 0, fouls: 0,
  startTime: Date.now(),
});

const BALLS: BallColor[] = ['red', 'yellow', 'green', 'brown', 'blue', 'pink', 'black'];

export function SessionLoggerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState<LoggerState>(emptyState());
  const [picker, setPicker] = useState<Picker>(null);
  const [sessionType, setSessionType] = useState<'match' | 'practice'>('practice');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<any>(null);
  const breakValRef = useRef<HTMLDivElement>(null);

  const startSession = async () => {
    if (!user) return;
    const id = await createSession(user.uid, sessionType);
    setState(s => ({ ...s, sessionId: id, started: true, startTime: Date.now() }));
  };

  // ── Shared pulse helper ────────────────────────────────────────
  const pulsePeak = () => {
    if (breakValRef.current) {
      breakValRef.current.classList.remove('logger__break-val--pulse');
      void breakValRef.current.offsetWidth;
      breakValRef.current.classList.add('logger__break-val--pulse');
    }
  };

  // ── Regular pot ───────────────────────────────────────────────
  const recordPot = useCallback((ball: BallColor) => {
    setPicker(null);
    setState(s => {
      const points = BALL_POINTS[ball];
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'pot', ball, points,
        timestamp: Date.now(), frameNumber: s.frameNum,
        breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak + points,
      };
      pulsePeak();
      return {
        ...s, shots: [...s.shots, shot], pots: s.pots + 1,
        currentBreak: s.currentBreak + points,
        currentBreakSeq: [...s.currentBreakSeq, ball],
      };
    });
  }, []);

  // ── Regular miss ──────────────────────────────────────────────
  const recordMiss = useCallback(() => {
    setState(s => {
      const committed = commitBreak(s, 'miss');
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'miss', timestamp: Date.now(),
        frameNumber: s.frameNum, breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return { ...committed, shots: [...committed.shots, shot], misses: s.misses + 1 };
    });
  }, []);

  // ── Safety ────────────────────────────────────────────────────
  const recordSafety = useCallback((outcome: SafetyOutcome) => {
    setPicker(null);
    setState(s => {
      const committed = commitBreak(s, 'safety');
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'safety', safetyOutcome: outcome,
        timestamp: Date.now(), frameNumber: s.frameNum,
        breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return { ...committed, shots: [...committed.shots, shot], safeties: s.safeties + 1 };
    });
  }, []);

  // ── Foul ──────────────────────────────────────────────────────
  const recordFoul = useCallback(() => {
    setState(s => {
      const committed = commitBreak(s, 'foul');
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'foul', timestamp: Date.now(),
        frameNumber: s.frameNum, breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return { ...committed, shots: [...committed.shots, shot], fouls: s.fouls + 1 };
    });
  }, []);

  // ── Long pot — ball selected after confirming pot ─────────────
  const recordLongPot = useCallback((ball: BallColor) => {
    setPicker(null);
    setState(s => {
      const points = BALL_POINTS[ball];
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'longPot', ball, points,
        timestamp: Date.now(), frameNumber: s.frameNum,
        breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak + points,
      };
      pulsePeak();
      return {
        ...s, shots: [...s.shots, shot], pots: s.pots + 1,
        currentBreak: s.currentBreak + points,
        currentBreakSeq: [...s.currentBreakSeq, ball],
      };
    });
  }, []);

  // ── Long miss ─────────────────────────────────────────────────
  const recordLongMiss = useCallback(() => {
    setPicker(null);
    setState(s => {
      const committed = commitBreak(s, 'miss');
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'longMiss', timestamp: Date.now(),
        frameNumber: s.frameNum, breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return { ...committed, shots: [...committed.shots, shot], misses: s.misses + 1 };
    });
  }, []);

  // ── Rest pot — ball selected after confirming pot ─────────────
  const recordRestPot = useCallback((ball: BallColor) => {
    setPicker(null);
    setState(s => {
      const points = BALL_POINTS[ball];
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'restPot', ball, points,
        timestamp: Date.now(), frameNumber: s.frameNum,
        breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak + points,
      };
      pulsePeak();
      return {
        ...s, shots: [...s.shots, shot], pots: s.pots + 1,
        currentBreak: s.currentBreak + points,
        currentBreakSeq: [...s.currentBreakSeq, ball],
      };
    });
  }, []);

  // ── Rest miss ─────────────────────────────────────────────────
  const recordRestMiss = useCallback(() => {
    setPicker(null);
    setState(s => {
      const committed = commitBreak(s, 'miss');
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'restMiss', timestamp: Date.now(),
        frameNumber: s.frameNum, breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return { ...committed, shots: [...committed.shots, shot], misses: s.misses + 1 };
    });
  }, []);

  // ── Snooker ───────────────────────────────────────────────────
  const recordSnooker = useCallback((outcome: EscapeOutcome) => {
    setPicker(null);
    setState(s => {
      const committed = outcome === 'fouled' ? commitBreak(s, 'foul') : s;
      const shot: Shot = {
        id: crypto.randomUUID(), type: 'snookered', escapeOutcome: outcome,
        timestamp: Date.now(), frameNumber: s.frameNum,
        breakNumber: s.currentBreakNum, breakRunningTotal: s.currentBreak,
      };
      return {
        ...committed,
        shots: [...committed.shots, shot],
        fouls: outcome === 'fouled' ? s.fouls + 1 : s.fouls,
      };
    });
  }, []);

  // ── Undo ──────────────────────────────────────────────────────
  const undoLast = useCallback(() => {
    setState(s => {
      if (!s.shots.length) return s;
      const last  = s.shots[s.shots.length - 1];
      const shots = s.shots.slice(0, -1);
      if (last.type === 'pot')       return { ...s, shots, pots: s.pots - 1, currentBreak: s.currentBreak - (last.points ?? 0), currentBreakSeq: s.currentBreakSeq.slice(0, -1) };
      if (last.type === 'miss')      return { ...s, shots, misses: s.misses - 1 };
      if (last.type === 'safety')    return { ...s, shots, safeties: s.safeties - 1 };
      if (last.type === 'foul')      return { ...s, shots, fouls: s.fouls - 1 };
      if (last.type === 'longPot')   return { ...s, shots, pots: s.pots - 1, currentBreak: s.currentBreak - (last.points ?? 1), currentBreakSeq: s.currentBreakSeq.slice(0, -1) };
      if (last.type === 'longMiss')  return { ...s, shots, misses: s.misses - 1 };
      if (last.type === 'restPot')   return { ...s, shots, pots: s.pots - 1, currentBreak: s.currentBreak - (last.points ?? 1), currentBreakSeq: s.currentBreakSeq.slice(0, -1) };
      if (last.type === 'restMiss')  return { ...s, shots, misses: s.misses - 1 };
      if (last.type === 'snookered') return { ...s, shots, fouls: last.escapeOutcome === 'fouled' ? s.fouls - 1 : s.fouls };
      return { ...s, shots };
    });
  }, []);

  const endFrame = useCallback(() => {
    setState(s => ({ ...commitBreak(s, 'end_of_frame'), frameNum: s.frameNum + 1 }));
  }, []);

  const endSession = async () => {
    if (!state.sessionId || !user) return;
    setSaving(true);
    const durationMinutes = Math.round((Date.now() - state.startTime) / 60000);
    const stats = computeSessionStats(
      state.sessionId!, user.uid, new Date(), state.shots, state.frames,
      durationMinutes, sessionType
    );
    await saveCompletedSession(state.sessionId!, { shots: state.shots, frames: state.frames, stats });
    setResult(stats);
    setSaving(false);
  };

  // ── Result screen ─────────────────────────────────────────────
  if (result) {
    const insights = generateInsights(result);
    return (
      <div className="result-page">
        <div className="result-header">
          <h1>Session Complete</h1>
          <p>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
        </div>

        <div className="result-grid">
          {[
            { label: 'Pot Rate',    value: `${result.potRate}%` },
            { label: 'High Break',  value: result.highBreak },
            { label: 'Avg Break',   value: result.avgBreak },
            { label: 'Pots',        value: result.totalPots },
            { label: 'Misses',      value: result.totalMisses },
            { label: 'Safety Rate', value: `${result.safetyRate}%` },
          ].map(({ label, value }) => (
            <div key={label} className="result-stat">
              <div className="result-stat__val">{value}</div>
              <div className="result-stat__label">{label}</div>
            </div>
          ))}
        </div>

        {(result.snookeredCount > 0 || result.totalLongAttempts > 0 || result.totalRestAttempts > 0) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {result.snookeredCount > 0 && (
              <StatPill label="Escapes" value={`${result.snookerEscapes}/${result.snookeredCount}`} sub={`${result.snookerEscapeRate}%`} />
            )}
            {result.totalLongAttempts > 0 && (
              <StatPill label="Long Pot" value={`${result.totalLongPots}/${result.totalLongAttempts}`} sub={`${result.longPotRate}%`} />
            )}
            {result.totalRestAttempts > 0 && (
              <StatPill label="Rest" value={`${result.totalRestPots}/${result.totalRestAttempts}`} sub={`${result.restSuccessRate}%`} />
            )}
          </div>
        )}

        {insights.length > 0 && (
          <>
            <div className="result-insights-title">Coaching Insights</div>
            {insights.map((ins: any, i: number) => (
              <InsightBox key={i} icon={ins.icon} text={ins.text} action={ins.action} />
            ))}
          </>
        )}

        <div className="share-card">
          <div className="share-card__logo">147coach</div>
          <div className="share-card__break">{result.highBreak || '—'}</div>
          <div className="share-card__label">Session Highest Break</div>
        </div>

        <div className="result-actions">
          <Button variant="outline" fullWidth onClick={() => { setState(emptyState()); setResult(null); }}>
            New Session
          </Button>
          <Button fullWidth onClick={() => navigate('/dashboard')}>→ Dashboard</Button>
        </div>
      </div>
    );
  }

  // ── Pre-start screen ──────────────────────────────────────────
  if (!state.started) {
    return (
      <div className="logger__start-screen">
        <div className="logger__start-icon">
          <Crosshair size={44} strokeWidth={1.25} color="var(--gold)" />
        </div>
        <h1 style={{ marginBottom: 8 }}>Log a Session</h1>
        <p style={{ marginBottom: 32, color: 'var(--chalk-dim)' }}>
          Choose session type, then tap after each shot.
        </p>
        <div className="logger__type-row">
          {(['practice', 'match'] as const).map(t => (
            <button
              key={t}
              onClick={() => setSessionType(t)}
              className={`logger__type-btn ${sessionType === t ? 'logger__type-btn--selected' : 'logger__type-btn--idle'}`}
            >
              {t === 'practice'
                ? <><Target size={14} strokeWidth={1.75} style={{ marginRight: 6, verticalAlign: 'middle' }} />Practice</>
                : <><Trophy size={14} strokeWidth={1.75} style={{ marginRight: 6, verticalAlign: 'middle' }} />Match</>
              }
            </button>
          ))}
        </div>
        <Button size="lg" onClick={startSession}>Start Session</Button>
      </div>
    );
  }

  // ── Active logger ─────────────────────────────────────────────
  const potAttempts = state.pots + state.misses;
  const potPct = potAttempts > 0 ? Math.round((state.pots / potAttempts) * 100) : null;

  // Reusable ball picker — label changes depending on context
  const BallPicker = ({ label, onSelect }: { label: string; onSelect: (b: BallColor) => void }) => (
    <div className="logger__ball-picker">
      <div className="logger__ball-picker-label">{label}</div>
      <div className="logger__ball-picker-row">
        {BALLS.map(ball => (
          <button key={ball} className="logger__ball-btn" onClick={() => onSelect(ball)}
            style={{ background: BALL_HEX[ball], color: ball === 'yellow' ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)' }}>
            {BALL_POINTS[ball]}
          </button>
        ))}
      </div>
      <div className="logger__ball-picker-cancel">
        <button className="logger__cancel-btn" onClick={() => setPicker(null)}>cancel</button>
      </div>
    </div>
  );

  // Reusable pot/miss outcome picker
  const OutcomePicker = ({ label, onPot, onMiss }: { label: string; onPot: () => void; onMiss: () => void }) => (
    <div className="logger__ball-picker">
      <div className="logger__ball-picker-label">{label}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <button className="logger__shot-btn logger__shot-btn--pot" onClick={onPot} style={{ padding: '18px' }}>
          <CheckCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
          Pot<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>It went in</span>
        </button>
        <button className="logger__shot-btn logger__shot-btn--miss" onClick={onMiss} style={{ padding: '18px' }}>
          <XCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
          Miss<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>Didn't go in</span>
        </button>
      </div>
      <div className="logger__ball-picker-cancel">
        <button className="logger__cancel-btn" onClick={() => setPicker(null)}>cancel</button>
      </div>
    </div>
  );

  return (
    <div className="logger">
      {/* Counters */}
      <div className="logger__counters">
        {[
          { val: state.frameNum, label: 'Frame' },
          { val: state.shots.length, label: 'Shots' },
          { val: potPct !== null ? `${potPct}%` : '—', label: 'Pot %' },
        ].map(({ val, label }) => (
          <div key={label} className="logger__counter">
            <div className="logger__counter-val">{val}</div>
            <div className="logger__counter-label">{label}</div>
          </div>
        ))}
      </div>

      {/* Break display */}
      <div className="logger__break-display">
        <div className="logger__break-label">Current Break</div>
        <div ref={breakValRef} className="logger__break-val">{state.currentBreak}</div>
        <div className="logger__break-seq">
          {state.currentBreakSeq.map((ball, i) => (
            <div key={i} className="logger__ball-dot" style={{ background: BALL_HEX[ball], color: ball === 'yellow' ? 'rgba(0,0,0,0.7)' : '#fff' }}>
              {BALL_POINTS[ball]}
            </div>
          ))}
        </div>
      </div>

      {/* ── Regular ball picker ── */}
      {picker === 'ball' && (
        <BallPicker label="Which ball?" onSelect={recordPot} />
      )}

      {/* ── Long — outcome first, then ball if pot ── */}
      {picker === 'long' && (
        <OutcomePicker
          label="Long pot — did it go in?"
          onPot={() => setPicker('longBall')}
          onMiss={recordLongMiss}
        />
      )}
      {picker === 'longBall' && (
        <BallPicker label="Long pot — which ball?" onSelect={recordLongPot} />
      )}

      {/* ── Rest — outcome first, then ball if pot ── */}
      {picker === 'rest' && (
        <OutcomePicker
          label="Rest shot — did it go in?"
          onPot={() => setPicker('restBall')}
          onMiss={recordRestMiss}
        />
      )}
      {picker === 'restBall' && (
        <BallPicker label="Rest shot — which ball?" onSelect={recordRestPot} />
      )}

      {/* ── Safety picker ── */}
      {picker === 'safety' && (
        <div className="logger__ball-picker">
          <div className="logger__ball-picker-label">Safety outcome?</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <button className="logger__shot-btn" onClick={() => recordSafety('good')}
              style={{ background: 'rgba(46,204,113,0.1)', borderColor: 'rgba(46,204,113,0.3)', color: 'var(--success)', padding: '18px' }}>
              <CheckCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
              Good<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>Left in trouble</span>
            </button>
            <button className="logger__shot-btn" onClick={() => recordSafety('bad')}
              style={{ background: 'rgba(231,76,60,0.08)', borderColor: 'rgba(231,76,60,0.25)', color: '#e57373', padding: '18px' }}>
              <XCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
              Poor<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>Left a pot on</span>
            </button>
          </div>
          <div className="logger__ball-picker-cancel">
            <button className="logger__cancel-btn" onClick={() => setPicker(null)}>cancel</button>
          </div>
        </div>
      )}

      {/* ── Snooker picker ── */}
      {picker === 'snooker' && (
        <div className="logger__ball-picker">
          <div className="logger__ball-picker-label">Snookered — what happened?</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <button className="logger__shot-btn" onClick={() => recordSnooker('escaped')}
              style={{ background: 'rgba(46,204,113,0.1)', borderColor: 'rgba(46,204,113,0.3)', color: 'var(--success)', padding: '18px' }}>
              <CheckCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
              Escaped<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>No foul</span>
            </button>
            <button className="logger__shot-btn" onClick={() => recordSnooker('fouled')}
              style={{ background: 'rgba(231,76,60,0.08)', borderColor: 'rgba(231,76,60,0.25)', color: '#e57373', padding: '18px' }}>
              <XCircle size={20} strokeWidth={1.75} style={{ marginBottom: 4 }} /><br />
              Fouled<br /><span style={{ fontSize: '0.62rem', opacity: 0.8 }}>Points conceded</span>
            </button>
          </div>
          <div className="logger__ball-picker-cancel">
            <button className="logger__cancel-btn" onClick={() => setPicker(null)}>cancel</button>
          </div>
        </div>
      )}

      {/* ── Main shot grid ── */}
      {picker === null && (
        <div className="logger__shot-grid">
          <ShotBtn className="logger__shot-btn--pot" onClick={() => setPicker('ball')}    icon={<Circle size={20} strokeWidth={1.75} />}         label="Pot" />
          <ShotBtn className="logger__shot-btn--miss" onClick={recordMiss}                icon={<X size={20} strokeWidth={2} />}                   label="Miss" />
          <ShotBtn onClick={() => setPicker('safety')}                                    icon={<Shield size={18} strokeWidth={1.75} />}            label="Safety" />
          <ShotBtn onClick={recordFoul}                                                   icon={<AlertTriangle size={18} strokeWidth={1.75} />}     label="Foul" />
          <ShotBtn onClick={() => setPicker('long')}                                      icon={<ArrowLeftRight size={18} strokeWidth={1.75} />}    label="Long" />
          <ShotBtn onClick={() => setPicker('snooker')}                                   icon={<Lock size={18} strokeWidth={1.75} />}              label="Snookered" />
          <ShotBtn onClick={() => setPicker('rest')}                                      icon={<Shuffle size={18} strokeWidth={1.75} />}           label="Rest" />
        </div>
      )}

      {/* Controls */}
      <div className="logger__controls">
        <button className="logger__ctrl-btn" onClick={undoLast}>
          <RotateCcw size={13} strokeWidth={2} style={{ marginRight: 5, verticalAlign: 'middle' }} />
          Undo
        </button>
        <button className="logger__ctrl-btn" onClick={endFrame}>End Frame</button>
        <button className="logger__ctrl-btn logger__ctrl-btn--end" onClick={endSession} disabled={saving || state.shots.length === 0}>
          {saving ? 'Saving…' : <><span>End Session</span><ChevronRight size={14} strokeWidth={2.5} style={{ marginLeft: 4, verticalAlign: 'middle' }} /></>}
        </button>
      </div>

      {/* Shot log */}
      <div className="logger__log">
        {state.shots.slice(-8).reverse().map(shot => (
          <div key={shot.id} className="logger__log-entry">
            <div className="logger__log-dot" style={{ background: shotColor(shot.type, shot.ball) }} />
            <span className="logger__log-text">{shotLabel(shot)}</span>
            {(shot.type === 'pot' || shot.type === 'longPot' || shot.type === 'restPot') && shot.breakRunningTotal > 0 && (
              <span className="logger__log-break">Break: {shot.breakRunningTotal}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────

function ShotBtn({ onClick, className, icon, label, style }: {
  onClick: () => void;
  className?: string;
  icon: React.ReactNode;
  label: string;
  style?: React.CSSProperties;
}) {
  return (
    <button
      className={`logger__shot-btn${className ? ` ${className}` : ''}`}
      onClick={onClick}
      style={style}
    >
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        {icon}
        <span style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</span>
      </span>
    </button>
  );
}

function StatPill({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div style={{
      flex: 1, minWidth: 90,
      background: 'rgba(255,255,255,0.03)', border: 'var(--border-card)',
      borderRadius: 8, padding: '10px 14px',
      display: 'flex', flexDirection: 'column', gap: 2,
    }}>
      <div style={{ fontSize: '0.58rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--chalk-faint)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', fontWeight: 800, color: 'var(--gold)', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.62rem', color: 'var(--chalk-faint)' }}>{sub}</div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

function commitBreak(s: LoggerState, endedBy: BreakRecord['endedBy']): LoggerState {
  if (s.currentBreak === 0) return s;
  const breakRec: BreakRecord = {
    breakNumber: s.currentBreakNum, frameNumber: s.frameNum,
    score: s.currentBreak, ballSequence: [...s.currentBreakSeq], endedBy,
  };
  const existingFrame = s.frames.find(f => f.frameNumber === s.frameNum);
  const newFrames = existingFrame
    ? s.frames.map(f => f.frameNumber === s.frameNum ? { ...f, breaks: [...f.breaks, breakRec] } : f)
    : [...s.frames, {
        frameNumber: s.frameNum, breaks: [breakRec], highBreak: s.currentBreak,
        totalPots: s.pots, totalMisses: s.misses, totalSafeties: s.safeties, totalFouls: s.fouls,
      }];
  return { ...s, frames: newFrames, currentBreak: 0, currentBreakSeq: [], currentBreakNum: s.currentBreakNum + 1 };
}

function shotColor(type: string, ball?: BallColor): string {
  if ((type === 'pot' || type === 'longPot' || type === 'restPot') && ball) return BALL_HEX[ball];
  if (type === 'miss' || type === 'longMiss' || type === 'restMiss') return 'var(--danger)';
  if (type === 'safety')    return '#3498db';
  if (type === 'foul')      return 'var(--warning)';
  if (type === 'snookered') return '#e67e22';
  return 'var(--chalk-faint)';
}

function shotLabel(shot: Shot): string {
  if (shot.type === 'pot')       return `Potted ${shot.ball} (+${shot.points})`;
  if (shot.type === 'miss')      return 'Miss';
  if (shot.type === 'safety')    return shot.safetyOutcome === 'good' ? 'Safety — Good' : shot.safetyOutcome === 'bad' ? 'Safety — Poor' : 'Safety';
  if (shot.type === 'foul')      return 'Foul';
  if (shot.type === 'longPot')   return `Long pot — ${shot.ball} (+${shot.points})`;
  if (shot.type === 'longMiss')  return 'Long pot — miss';
  if (shot.type === 'restPot')   return `Rest — ${shot.ball} (+${shot.points})`;
  if (shot.type === 'restMiss')  return 'Rest — miss';
  if (shot.type === 'snookered') return shot.escapeOutcome === 'escaped' ? 'Snookered — escaped' : 'Snookered — fouled';
  return shot.type;
}